import { FDRStatus, FieldFDRStats, OrgUserModel, OrgUserSearchResult } from '@cibo/core'
import { LineLimitTypography, Markdown, intuitiveFromNow, useFigures } from '@cibo/ui'
import { Link, Stack, Typography, styled } from '@mui/material'
import { GridColDef, GridRenderCellParams } from '@mui/x-data-grid-pro'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

interface UseOrgUserFdrStatsColumnProps {
  status: FDRStatus
}

const CellMarkdown = styled(Markdown)(() => ({
  '&.MuiTypography-root': {
    marginBottom: 0,
  },
}))

export const useOrgUserFdrStatsColumn = ({ status }: UseOrgUserFdrStatsColumnProps) => {
  const { t } = useTranslation('@cibo/profile/PaginatedUsersTable')
  const { acres, fields } = useFigures()

  return useMemo(
    () =>
      ({
        field: status,
        headerName: t('status', { context: status }),
        minWidth: 80,
        sortable: false,
        disableColumnMenu: true,
        valueGetter: (_, row: OrgUserSearchResult) =>
          row.fdrStats?.find(fdrStat => fdrStat.status === status),
        renderCell: ({
          value,
        }: GridRenderCellParams<OrgUserSearchResult, FieldFDRStats | undefined>) =>
          !!value ? (
            <Stack>
              <Typography variant="body2">{value.acres ? acres(value.acres) : null}</Typography>
              <Typography variant="caption">
                {value.fields ? fields(value.fields) : null}
              </Typography>
            </Stack>
          ) : null,
      } as GridColDef),
    []
  )
}
export interface UseOrgUserColumnsProps {
  onClickOrganization?: (params: { organizationId?: string }) => void
}

export const useOrgUserColumns = ({ onClickOrganization }: UseOrgUserColumnsProps) => {
  const { t } = useTranslation('@cibo/profile/PaginatedUsersTable')
  const { acres } = useFigures()

  return useMemo(
    () => ({
      grower: {
        field: 'familyName', // this is the sorting field for the endpoint, value getter overrides it for display
        headerName: t('persona_grower'),
        minWidth: 200,
        flex: 1,
        filterable: false,
        valueGetter: (_: any, row: OrgUserSearchResult) => {
          const user = new OrgUserModel(row)
          return { name: user.searchResultsDisplayNameMarkdown, org: row.org?.label }
        },
        renderCell: ({ value }: GridRenderCellParams<any, { name: string; org?: string }>) => (
          <Stack>
            <CellMarkdown>{value?.name}</CellMarkdown>
            {value?.org ? <Typography variant="caption">{value?.org}</Typography> : null}
          </Stack>
        ),
      },
      network: {
        field: 'org',
        headerName: t('growerNetwork'),
        minWidth: 150,
        flex: 1,
        filterable: false,
        valueGetter: (_: any, row: OrgUserSearchResult) => row.org?.label ?? '-',
      },
      name: {
        field: 'familyName',
        headerName: t('name'),
        minWidth: 200,
        flex: 1,
        filterable: false,
        valueGetter: (_: any, row: OrgUserSearchResult) => {
          if (!row.givenName && !row.familyName) {
            return ''
          }
          return `${row.givenName} ${row.familyName}`
        },
        renderCell: ({ row, value }: GridRenderCellParams<OrgUserSearchResult, string>) => {
          return (
            <Stack overflow="hidden" data-testid={`user-${row.email}`}>
              <LineLimitTypography lineLimit={1} variant="body2">
                {!!value ? value : '--'}
              </LineLimitTypography>
              {!!row.persona && (
                <LineLimitTypography lineLimit={1} variant="caption">
                  {t('persona', { context: row.persona, ns: '@cibo/profile/PersonaCards' })}
                </LineLimitTypography>
              )}
            </Stack>
          )
        },
      },
      email: {
        field: 'email',
        headerName: t('email'),
        minWidth: 250,
        flex: 1,
        filterable: false,
        valueGetter: (_: any, row: OrgUserSearchResult) => {
          const user = new OrgUserModel(row)
          return user.searchResultsDisplayEmailMarkdown
        },
        title: (value: string) => value,
        renderCell: ({ value }: GridRenderCellParams<any, string>) => (
          <CellMarkdown typographyProps={{ title: value, variant: 'caption', fontSize: '14px' }}>
            {value}
          </CellMarkdown>
        ),
      },
      organization: {
        field: 'org',
        headerName: t('organization'),
        minWidth: 200,
        flex: 1,
        filterable: false,
        valueGetter: (value: OrgUserSearchResult['org']) => value?.label,
        renderCell: (params: GridRenderCellParams<any, string, OrgUserSearchResult>) =>
          onClickOrganization && params.row.org?.id ? (
            <Link
              color="secondary"
              onClick={(event: any) => {
                event.stopPropagation()
                onClickOrganization({ organizationId: params.row.org?.id })
              }}
              underline="hover"
            >
              {params.value}
            </Link>
          ) : (
            params.value
          ),
      },
      totalFields: {
        type: 'number',
        field: 'totalFields',
        headerName: t('fields'),
        filterable: false,
        valueGetter: (_: any, row: OrgUserSearchResult) => row.resources?.fields,
        renderCell: (params: GridRenderCellParams<any, number | undefined>) => (
          <Typography variant="body2">{params.value || '--'}</Typography>
        ),
      },
      acres: {
        type: 'number',
        field: 'acres',
        headerName: t('acres'),
        filterable: false,
        valueGetter: (_: any, row: OrgUserSearchResult) => row.resources?.acres,
        renderCell: (params: GridRenderCellParams<any, number | undefined>) => (
          <Typography variant="body2">{acres(params.value, undefined, false)}</Typography>
        ),
      },
      lastLoginDate: {
        field: 'lastLoginDate',
        sortable: false, // while this works as a param, the order returned is incorrect
        filterable: false,
        headerName: t('lastLoginDate'),
        valueFormatter: (value: string) => (!!value ? intuitiveFromNow(value) : t('never')),
      },
    }),
    [t]
  )
}
